import { useEffect, useState } from 'react';

// Mobile-only fit for a hero's rotating big word: each word is sized to fill
// the free width — short words grow (up to MAX_FIT× their base size), long
// ones shrink (down to MIN_FIT×) so they never run off the screen. The word box reserves the height of the
// biggest fitted word, so the layout never jumps between words.
//
// words:     every word the hero cycles through
// wordClass: the rotating word's className (font, base size); words are
//            measured inside the box, so em-based sizes resolve as rendered
// Returns { measureBox, fitFor(word), boxHeight, active }: pass `measureBox` as
// the word box's ref (destructure it out, so the lint rule doesn't treat the
// rest as a ref). A callback ref, so a box that mounts late (after the
// splash, on a tab switch) is still measured. active only below 768px.
const MAX_FIT = 1.6;
const MIN_FIT = 0.4;
const FILL = 0.82;
// Word box height per unit of fit (em of the h1): just the glyphs plus a
// tight margin, so the topline and description sit close to the word.
const BOX = 0.85;

const useHeroWordFit = (words, wordClass) => {
  const [box, setBox] = useState(null);
  const [fits, setFits] = useState(null);
  const key = words.join('|');

  useEffect(() => {
    if (!box) return;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      if (window.innerWidth >= 768) { setFits(null); return; }
      const probe = document.createElement('span');
      probe.className = wordClass;
      probe.style.cssText = 'position:absolute;visibility:hidden;left:0;top:0;transform:none;translate:none;padding:0';
      box.appendChild(probe);
      // The screen, not the box: a box can be wider than the screen when a
      // long word's spacer sets its width.
      const avail = Math.min(box.getBoundingClientRect().width, window.innerWidth) * FILL;
      const next = {};
      for (const w of key.split('|')) {
        probe.textContent = w;
        const width = probe.getBoundingClientRect().width || 1;
        next[w] = Math.max(MIN_FIT, Math.min(MAX_FIT, avail / width));
      }
      probe.remove();
      setFits(next);
    };
    document.fonts.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => { alive = false; window.removeEventListener('resize', measure); };
  }, [box, key, wordClass]);

  return {
    measureBox: setBox,
    active: !!fits,
    fitFor: (w) => (fits?.[w] ?? 1),
    boxHeight: fits ? `${(BOX * Math.max(...Object.values(fits))).toFixed(3)}em` : undefined,
  };
};

export default useHeroWordFit;
